// ./src/contexts/ParkingSessionsContext.tsx
//
// Context wrapper around the useParkingSessions hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, stats, toolbar)
//   to share the same parking sessions state without prop-drilling.
// - Each page creates its own <ParkingSessionsProvider> with its own config,
//   so ParkingSessionsPage filters don't affect DashboardPage, etc.
// - The underlying useParkingSessions hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <ParkingSessionsProvider autoLoad={false} pageSize={20}>
//     <ParkingSessionsPageContent />
//     <ParkingSessionEditDrawer />
//   </ParkingSessionsProvider>
//
//   // In any child component:
//   const { sessions, isLoading, start, ... } = useParkingSessionsContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalParkingSessionsContext();
//   const startFn = context?.start ?? props.onStart;

import React, { createContext, useContext } from "react";
import {
  useParkingSessions,
  UseParkingSessionsOptions,
  UseParkingSessionsReturn,
} from "@/hooks/useParkingSessions";

// ============================================
// Context
// ============================================

const ParkingSessionsContext =
  createContext<UseParkingSessionsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface ParkingSessionsProviderProps
  extends UseParkingSessionsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useParkingSessions into a context provider.
 *
 * Each instance creates its own independent useParkingSessions store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <ParkingSessionsProvider autoLoad={false} pageSize={20}>
 *   <ParkingSessionsPageContent />
 * </ParkingSessionsProvider>
 */
export const ParkingSessionsProvider: React.FC<
  ParkingSessionsProviderProps
> = ({ children, ...options }) => {
  const parkingSessions = useParkingSessions(options);

  return (
    <ParkingSessionsContext.Provider value={parkingSessions}>
      {children}
    </ParkingSessionsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the ParkingSessionsContext. Throws if used outside a ParkingSessionsProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. ParkingSessionList, ParkingSessionStats).
 */
export const useParkingSessionsContext = (): UseParkingSessionsReturn => {
  const context = useContext(ParkingSessionsContext);

  if (!context) {
    throw new Error(
      "useParkingSessionsContext must be used within a <ParkingSessionsProvider>. " +
      "Wrap your component tree with <ParkingSessionsProvider> or use useOptionalParkingSessionsContext() instead."
    );
  }

  return context;
};

/**
 * Access the ParkingSessionsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. ParkingSessionEditDrawer which works both inside ParkingSessionsPage's
 * provider and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalParkingSessionsContext();
 * const startFn = context?.start ?? props.onStart;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalParkingSessionsContext =
  (): UseParkingSessionsReturn | null => {
    return useContext(ParkingSessionsContext);
  };